// Equipment wire protocol — Phase 7 Block C.
//
// Shape-only module mirroring shared/durability-messages.ts. The Envelope
// generic is inlined to avoid a circular import with shared/messages.ts
// (which re-exports the union extension below).
//
// Phase 7 Block C ships weapon-only — armor + offhand defer to Phase 8.
// See DECISIONS.

import type { WeaponCategory } from "./abilities.ts";

type Envelope<T extends string, P> = {
  type: T;
  ts: number;
  payload: P;
  seq?: number;
  eventId?: string;
};

// -----------------------------------------------------------------------
// Client → Server
// -----------------------------------------------------------------------

// Equip the weapon at the supplied catalog id. Server resolves to the
// first matching inventory rowid (slot_index ASC) and writes
// player.equipped_weapon_inventory_rowid. Failures surface as `error`
// envelopes with action=equipItem so the inventory modal can clear
// pending state.
export type ClientEquipItem = Envelope<"equipItem", { itemId: string }>;

// Unequip the currently-equipped weapon. No itemId — there's at most
// one equipped weapon. No-op if nothing is equipped.
export type ClientUnequipItem = Envelope<"unequipItem", Record<string, never>>;

// -----------------------------------------------------------------------
// Server → Client (DURABLE)
// -----------------------------------------------------------------------

// Snapshot of the equipped weapon, or null when nothing is equipped.
// `inventoryRowid` pins the specific row so durability + the hotbar
// "E" badge can address it. Sent on every equip/unequip success.
export type EquippedItemReadout = {
  itemId: string;
  inventoryRowid: number;
  weaponType: WeaponCategory;
  durabilityCurrent: number;
  durabilityMax: number;
};

export type ServerEquippedItemUpdated = Envelope<
  "equippedItemUpdated",
  { equipped: EquippedItemReadout | null }
>;

// -----------------------------------------------------------------------
// Builders
// -----------------------------------------------------------------------

export const buildEquipItemMessage = (itemId: string): ClientEquipItem => ({
  type: "equipItem",
  ts: Date.now(),
  payload: { itemId },
});

export const buildUnequipItemMessage = (): ClientUnequipItem => ({
  type: "unequipItem",
  ts: Date.now(),
  payload: {},
});

export const buildEquippedItemUpdatedMessage = (
  equipped: EquippedItemReadout | null,
): ServerEquippedItemUpdated => ({
  type: "equippedItemUpdated",
  ts: Date.now(),
  payload: { equipped },
});
